import { TextField, WithStyles, Typography, Divider, Button } from '@material-ui/core';
import { createStyles, withStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import { PaginatedDataDTO } from '@bottega52/commons-pagination';
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { IKeyInDTO } from '../../repository/jago/model/input/IKeyInDTO';
import { ICylinderInDTO } from '../../repository/jago/model/input/ICylinderInDTO';
import { IMechanicalCylinderKeyRelationInDTO } from '../../repository/jago/model/input/IMechanicalCylinderKeyRelationInDTO';
import { IMechanicalCylinderKeyRelationsRequestParamsDTO } from '../../repository/jago/model/output/RequestParamsDTOs';
import { IState } from '../../redux/store';
import * as MksActions from '../../redux/mks/mks.actions';
import * as ModalsActions from '../../redux/modals/modals.actions';
import * as CreditsActions from '../../redux/credits/credits.actions';
import { ModalTypes } from '../Modals/ModalTypes';
import MksCylinderForm from './MksCylinderForm';
import AssociatedCylindersTable from '../MKS/AssociatedCylindersTable';
import ReduxLanguage from '../ReduxLanguage';
import { getDomainsOptions } from '../../repository/utils';
import { IDomainOptionDTO } from '../../repository/jago/model/input/IDomainOptionDTO';
import { MKSRequestTypeOptions, MKSRequestStatusOptions } from '../MKS/MKSRequestsTable';
import MKSRequestForm from './MKSRequestForm';
import AbilityProvider from '../../services/PermissionService/AbilityProvider';
import { PERMISSIONS } from '../../services/PermissionService/PermissionConstants';
import { IWalletPlantsRequestParamsDTO } from '../../repository/jago/model/output/RequestParamsDTOs';
import { IMKSRequestStatusChangeDTO } from '../../repository/jago/model/output/IMKSRequestDTO';

const styles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: 20,
    gap: 10
  },
  formField: {
    marginBottom: 10
  },
  sectionTitle: {
    marginTop: 0,
    marginBottom: 0,
    fontWeight: 'bold',
  },
  relationsList: {
    maxHeight: 300,
    overflow: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
  },
  actionButton: {
    width: 'fit-content',
    color: 'white',
    marginLeft: 20,
    padding: 4,
    paddingLeft: 10,
    paddingRight: 10,
    fontWeight: 'bold'
  },
  actionButtonText: {
    margin: 0,
    fontWeight: 'bold',
  },
  icon: {
    fontSize: 15,
    marginRight: 5,
  },
});

function mapStateToProps(state: IState) {
  return {
    relations: state.mks.relations.data,
    selectedDomain: state.mks.selectedDomain,
  };
}

const connector = connect(mapStateToProps);
type IReduxProps = ConnectedProps<typeof connector>;

export interface IMksKeyFormProps extends WithStyles<typeof styles>, RouteComponentProps {
  keyData?: IKeyInDTO | undefined;
  receivedHostnameFromMksRequestForm?: string | undefined;
  hideDuplicateButton?: boolean;
}

export interface IMksKeyFormState {
  isLoadingRelations: boolean;
  rowsPerPage: number;
  cylinderTypeFilter: string;
  doorNameFilter: string;
  allRelations: IMechanicalCylinderKeyRelationInDTO[];
  filteredRelations: IMechanicalCylinderKeyRelationInDTO[];
  currentPage: number;
}

export class MksKeyForm extends React.Component<IMksKeyFormProps & IReduxProps, IMksKeyFormState> {

  constructor(props: IMksKeyFormProps & IReduxProps) {
    super(props);
    this.state = {
      isLoadingRelations: false,
      rowsPerPage: 100,
      cylinderTypeFilter: '',
      doorNameFilter: '',
      allRelations: [],
      filteredRelations: [],
      currentPage: 0,
    };
  }

  public async componentDidMount() {
    await this.fetchAllRelations();
  }

  private async fetchAllRelations() {
    const { keyData, selectedDomain, receivedHostnameFromMksRequestForm, dispatch } = this.props;
    
    if (!keyData || (!selectedDomain && !receivedHostnameFromMksRequestForm)) return;

    try {
      this.setState({ isLoadingRelations: true });
      const params: IMechanicalCylinderKeyRelationsRequestParamsDTO = {
        hostname: receivedHostnameFromMksRequestForm ? receivedHostnameFromMksRequestForm : selectedDomain.value.hostname,
        keyIds: [keyData.id],
        page: 0,
        pageSize: 10000,
      };
      const result = await dispatch<any>(MksActions.fetchMechanicalCylinderKeyRelations(params));
      this.setState({ 
        allRelations: result.content || [],
        filteredRelations: result.content || [],
        isLoadingRelations: false 
      });
    } catch (error) {
      this.setState({ isLoadingRelations: false });
    }
  }

  private filterRelations = (cylinderType: string, doorName: string) => {
    const { allRelations } = this.state;
    let filtered = [...allRelations];

    if (cylinderType.trim()) {
      filtered = filtered.filter(relation => 
        relation.cylinder.type?.toLowerCase().includes(cylinderType.toLowerCase())
      );
    }

    if (doorName.trim()) {
      filtered = filtered.filter(relation => 
        relation.cylinder.doorName?.toLowerCase().includes(doorName.toLowerCase())
      );
    }

    this.setState({ 
      filteredRelations: filtered,
      cylinderTypeFilter: cylinderType,
      doorNameFilter: doorName,
      currentPage: 0
    });
  }

  public onSearchCylinders = (cylinderType: string, doorName: string) => {
    this.filterRelations(cylinderType, doorName);
  }

  public onFetchCylindersPerPage = (page: number) => {
    this.setState({ currentPage: page });
  }

  public onPageSizeChange = (pageSize: number) => {
    this.setState({ rowsPerPage: pageSize, currentPage: 0 });
  }

  public onCylinderClick = async (cylinderData: ICylinderInDTO) => {
    const { dispatch } = this.props;
    
    await dispatch<any>(ModalsActions.hideModal(`MKS_CYLINDER_DETAILS_MODAL`));
    dispatch<any>(MksActions.setSelectedCylinder(cylinderData));
    dispatch<any>(ModalsActions.showModal(`MKS_CYLINDER_DETAILS_MODAL`, {
      modalType: ModalTypes.OPERATIONAL_VIEW_MODAL,
      modalProps: {
        content: <MksCylinderForm cylinderData={cylinderData} />,
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`MKS_CYLINDER_DETAILS_MODAL`)),
      }
    }));
  }

  public onCreateDuplicateKeyRequest = async () => {
    const { dispatch, keyData, selectedDomain } = this.props;
    const { history } = this.props;
    
    if (!keyData || !selectedDomain) return;

    // Verifica permessi
    if (!AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.MKS_CREATE_REQUEST)) {
      return;
    }

    try {
      // Ottieni il plant dal domain
      const params: IWalletPlantsRequestParamsDTO = { 
        page: 0, 
        pageSize: 1, 
        hostname: selectedDomain.value.hostname, 
        walletId: undefined 
      };
      const domains = await dispatch<any>(CreditsActions.fetchDomainsSearch(params));
      const domainsOptions = getDomainsOptions(domains) as IDomainOptionDTO[];
      
      if (domainsOptions.length === 0) {
        return;
      }

      const plantOption = domainsOptions[0];
      
      // Prepara i dati iniziali - converti IKeyInDTO in IMKSRequestDataKey
      const keyForRequest = {
        id: keyData.id,
        externalId: keyData.externalId,
        keyFunction: keyData.keyFunction || '',
        designation: keyData.designation || ''
      };
      
      // Nuovo formato: usa keysDuplicates array invece di key singolo
      const initialData: { [key: string]: string } = {
        keysDuplicates: JSON.stringify([{ 
          key: keyForRequest, 
          quantity: keyData.quantity || 1 
        }]),
      };

      // Chiudi il modal corrente e apri il form di richiesta
      await dispatch<any>(ModalsActions.hideModal(`MKS_KEY_DETAILS_MODAL`));
      
      dispatch<any>(ModalsActions.showModal(`MKS_REQUEST_FORM_VIEW`, {
        modalType: ModalTypes.OPERATIONAL_VIEW_MODAL_BACKDROP,
        modalProps: {
          content: (
            <MKSRequestForm
              initialRequestType={MKSRequestTypeOptions.KEY_DUPLICATION}
              initialPlant={plantOption}
              initialData={initialData}
              onCreateEditMKSRequest={async (request) => {
                try {
                  await dispatch<any>(MksActions.createMKSRequest(request as any));
                  dispatch<any>(ModalsActions.hideModal(`MKS_REQUEST_FORM_VIEW`));
                  if (history.location.pathname!=='/mksRequests') {
                    history.push('/mksRequests')
                    history.go(0)
                  }
                } catch (error) {
                  console.error('Error creating request:', error);
                }
              }}
              onCreateAndSendRequest={async (request) => {
                try {
                  const createdRequest = await dispatch<any>(MksActions.createMKSRequest(request));
                  if (createdRequest && createdRequest.id) {
                    const statusChangeData: IMKSRequestStatusChangeDTO = {
                      status: MKSRequestStatusOptions.SENT
                    };
                    await dispatch<any>(MksActions.changeMKSRequestStatus(createdRequest.id, statusChangeData));
                  }
                  dispatch<any>(ModalsActions.hideModal(`MKS_REQUEST_FORM_VIEW`));
                  if (history.location.pathname!=='/mksRequests') {
                    history.push('/mksRequests')
                    history.go(0)
                  }
                } catch (error) {
                  console.error('Error creating and sending request:', error);
                }
              }}
            />
          ),
          titleMessageKey: 'mks.newRequest',
        }
      }));
    } catch (error) {
      console.error('Error creating duplicate key request:', error);
    }
  }

  public render() {
    const { classes, keyData, hideDuplicateButton } = this.props;
    const { isLoadingRelations, rowsPerPage, filteredRelations, currentPage } = this.state;

    if (!keyData) {
      return (
        <div className={classes.container}>
          <ReduxLanguage languageKey="mks.noKeySelected" />
        </div>
      );
    }

    
    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedRelations = filteredRelations.slice(startIndex, endIndex);
    
    
    const relationsData: PaginatedDataDTO<IMechanicalCylinderKeyRelationInDTO> = {
      content: paginatedRelations,
      pagination: {
        number: currentPage,
        size: rowsPerPage,
        totalElements: filteredRelations.length,
        totalPages: Math.ceil(filteredRelations.length / rowsPerPage)
      }
    };

    const hasPermission = AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.MKS_CREATE_REQUEST);

    return (
      <div className={classes.container}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <Typography variant="h6" className={classes.sectionTitle}><ReduxLanguage languageKey="mks.keyDetails" /></Typography>
          {hasPermission && !hideDuplicateButton && (
            <Button
              variant="contained"
              className={classes.actionButton}
              style={{ backgroundColor: '#5AC0B1', flexShrink: 0 }}
              onClick={this.onCreateDuplicateKeyRequest}
            >
              <AddIcon className={classes.icon} />
              <h5 className={classes.actionButtonText}><ReduxLanguage languageKey="mks.createDuplicateKeyRequest" /></h5>
            </Button>
          )}
        </div>
        <div style={{ backgroundColor: '#f0f0f0', padding: 10, borderRadius: 10 }}>
          <table width="100%" style={{ marginBottom: 10 }}>
            <tbody>
              <tr>
                <td style={{ padding: '12px 20px 12px 0', verticalAlign: 'top' }}>
                  <h3 style={{ margin: 0, fontWeight: 'bold', fontSize: '16px' }}>
                    <ReduxLanguage languageKey="mks.id" />
                  </h3>
                  <h3 style={{ margin: 0, fontWeight: 'normal', fontSize: '16px' }}>
                    {keyData.id || '---'}
                  </h3>
                </td>
                <td style={{ padding: '12px 20px 12px 0', verticalAlign: 'top' }}>
                  <h3 style={{ margin: 0, fontWeight: 'bold', fontSize: '16px' }}>
                    <ReduxLanguage languageKey="mks.externalId" />
                  </h3>
                  <h3 style={{ margin: 0, fontWeight: 'normal', fontSize: '16px' }}>
                    {keyData.externalId || '---'}
                  </h3>
                </td>
                <td style={{ padding: '12px 20px 12px 0', verticalAlign: 'top' }}>
                  {/* Empty cell for alignment */}
                </td>
              </tr>
              <tr>
                <td style={{ padding: '12px 20px 12px 0', verticalAlign: 'top' }}>
                  <h3 style={{ margin: 0, fontWeight: 'bold', fontSize: '16px' }}>
                    <ReduxLanguage languageKey="mks.keyFunction" />
                  </h3>
                  <h3 style={{ margin: 0, fontWeight: 'normal', fontSize: '16px' }}>
                    {keyData.keyFunction || '---'}
                  </h3>
                </td>
                <td colSpan={2} style={{ padding: '12px 20px 12px 0', verticalAlign: 'top' }}>
                  <h3 style={{ margin: 0, fontWeight: 'bold', fontSize: '16px' }}>
                    <ReduxLanguage languageKey="mks.designation" />
                  </h3>
                  <h3 style={{ margin: 0, fontWeight: 'normal', fontSize: '16px' }}>
                    {keyData.designation || '---'}
                  </h3>
                </td>
              </tr>
              <tr>
                <td style={{ padding: '12px 20px 12px 0', verticalAlign: 'top' }}>
                  <h3 style={{ margin: 0, fontWeight: 'bold', fontSize: '16px' }}>
                    <ReduxLanguage languageKey="mks.quantity" />
                  </h3>
                  <h3 style={{ margin: 0, fontWeight: 'normal', fontSize: '16px' }}>
                    {keyData.quantity || '---'}
                  </h3>
                </td>
                <td colSpan={2} style={{ padding: '12px 20px 12px 0', verticalAlign: 'top' }}>
                  {/* Empty cells for alignment */}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <Divider style={{ margin: '0px 0' }} />
        <Typography variant="h6" className={classes.sectionTitle}>
          <ReduxLanguage languageKey="mks.associatedCylinders" />
        </Typography>
        <AssociatedCylindersTable
          relations={relationsData}
          onFetchElementsOnPage={this.onFetchCylindersPerPage}
          onPageSizeChange={this.onPageSizeChange}
          onSearch={this.onSearchCylinders}
          onRowClick={this.onCylinderClick}
          rowsPerPage={rowsPerPage}
          isLoading={isLoadingRelations}
          useClientSideFiltering={true}
        />
      </div>
    );
  }
}

export default connector(withRouter(withStyles(styles)(MksKeyForm)));
